import React, { useCallback, useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '../app/hooks';
import { fetchActiveFeatureFlags } from '../app/store/featureFlagSlice';
import { ActiveFeatureFlag } from '../interfaces/featureFlag.interface';

/**
 * Central registry of client-gated feature-flag codenames. Add a flag's
 * codename here once and reference the constant everywhere, so gating is
 * typo-proof and greppable.
 */
export const FEATURE_FLAGS = {
  V2_COMPANY_PROFILE: 'can_view_v2_company_profile',
  DARK_THEME: 'can_view_dark_theme',
  AUTOFILL_QUESTIONNAIRE_ANSWERS: 'can_autofill_questionnaire_answers',
  AUTOFILL_ASSESSOR_ANSWERS: 'can_autofill_assessor_answers',
} as const;

export type FeatureFlagCodename = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS];

// Active (in-window) flags + their type targeting live in the featureFlags Redux
// slice — fetched on mount and refreshed on window focus (or a masquerade change),
// so a flag/targeting change shows up for the already-logged-in user without a
// re-login. Redux also gives the reset on logout/masquerade for free (see
// featureFlagSlice's extraReducers) and visibility in DevTools.
function useActiveFeatureFlags(): ActiveFeatureFlag[] | null {
  const dispatch = useAppDispatch();
  const { flags } = useAppSelector((state) => state.featureFlags);
  const { userId: masqueradeUserId } = useAppSelector((state) => state.masquerade);

  useEffect(() => {
    dispatch(fetchActiveFeatureFlags());
    const onFocus = () => { dispatch(fetchActiveFeatureFlags()); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [dispatch, masqueradeUserId]);

  return flags;
}

/**
 * Like {@link useFeatureFlag} but also reports `loaded` — whether the active
 * flags have been fetched yet. Useful for avoiding a flash when the gate drives
 * something page-wide (e.g. the theme): callers can hold a cached value until
 * `loaded` is true.
 */
export function useFeatureFlagState(codename: string): { enabled: boolean; loaded: boolean } {
  const flags = useActiveFeatureFlags();
  const { user } = useAppSelector((state) => state.user);

  // Individually-assigned permission always counts (known synchronously).
  if (user?.permissions?.includes(codename)) return { enabled: true, loaded: true };

  if (!flags) return { enabled: false, loaded: false };
  const flag = flags.find((f) => f.codename === codename);
  const enabled = !!flag && (flag.isPublic || (!!user?.type && flag.targetUserTypes.includes(user.type)));
  return { enabled, loaded: true };
}

/**
 * True when the current user should see the given client feature: the codename
 * is in their permission list (individual assignment), the flag is public, or
 * the flag targets their `user.type`. Fail-closed while flags are loading.
 *
 *   const showV2 = useFeatureFlag(FEATURE_FLAGS.V2_COMPANY_PROFILE);
 */
export function useFeatureFlag(codename: string): boolean {
  return useFeatureFlagState(codename).enabled;
}

/**
 * A callback that reports whether the current user has a flag, for use where a
 * hook per codename is impossible (e.g. checking a list of questionnaires).
 * Same resolution as {@link useFeatureFlag}.
 */
export function useFeatureFlagChecker(): (codename: string) => boolean {
  const flags = useActiveFeatureFlags();
  const { user } = useAppSelector((state) => state.user);

  return useCallback((codename: string) => {
    if (user?.permissions?.includes(codename)) return true;
    if (!flags) return false;
    const flag = flags.find((f) => f.codename === codename);
    return !!flag && (flag.isPublic || (!!user?.type && flag.targetUserTypes.includes(user.type)));
  }, [flags, user]);
}

interface FeatureGateProps {
  flag: string;
  children: React.ReactNode;
  /** Rendered when the user lacks the flag (defaults to nothing). */
  fallback?: React.ReactNode;
}

/**
 * Declarative gate — renders `children` only when the user has the flag.
 *
 *   <FeatureGate flag={FEATURE_FLAGS.V2_COMPANY_PROFILE} fallback={<Legacy />}>
 *     <BetaThing />
 *   </FeatureGate>
 */
export function FeatureGate({ flag, children, fallback = null }: FeatureGateProps) {
  return <>{useFeatureFlag(flag) ? children : fallback}</>;
}
